// Each service should expose a finder. The finder is responsible for
// configuring a service. In this case, the finder implements methods to
// configure the repositories that are mentioned in the configuration of the
// application.
import { SupportedConfigTypes } from "@/wc/registry/lib/service/repository/types.js";
import type {
    RepositoryAction,
    IRepositoryParameters,
    ActionPaths,
    IChoiceOptionsExtension,
    IConfig,
    ICacheConfig,
    IModifications,
    IModificationsObjects,
    IModificationsProperties,
    IRepositoryContainer,
    IRepository,
    IRepositoryActionOptions,
    IRepositoryManager,
    IRepositoryPropertiesChecker,
    IStekzSchema,
    IStekzFieldProperties,
    IFields,
} from "@/wc/registry/lib/service/repository/types";
import OpenAPIGeneratedRepository from "./openapi/base";
import { monitoring } from "@/lib/integrations/index";
import * as parsers from "@/wc/registry/lib/service/repository/parsers.js";
import { get as lodashGet, set as lodashSet } from "lodash";
import {
    findRepositoriesTargetingPathUsingMethod,
    findRepositoriesWithSameLengthPathUsingMethod,
} from "@/wc/registry/lib/service/repository/utils.js";
import { RepositoryCreationError } from "@/wc/registry/lib/service/repository/errors.js";

const actions = ["create", "update", "partialUpdate", "delete", "all", "get"];
const actionsWhosePropertiesCouldBeEnrichedWithLinks = [
    "create",
    "update",
    "partialUpdate",
];
const propertyToEnrichWithLinkEndsWith = "_id";

export function checkRepositoryConfiguration(
    repositoryName: string,
    type: string,
    configuration: IConfig
): boolean {
    if (type === "custom") {
        return true;
    }
    if (type === "openapi") {
        const { paths, openApiFileLocation } = configuration;
        if (!paths) {
            throw Error(
                `Cannot find 'paths' in the repository config for the '${repositoryName}' repository.`
            );
        }

        if (!openApiFileLocation) {
            throw Error(
                `Cannot find 'openApiFileLocation' in the repository config for the '${repositoryName}' repository.`
            );
        }
        return true;
    }

    throw Error(`The configuration for '${type}' is invalid.`);
}

export class RepositoryServiceContext {
    // These parameters are used to provide repositories with more information.
    // E.g. In the case of REST, this could mean that these parameters contain
    // a parameter that needs to be added to the url in a request.
    props: IRepositoryParameters = {};
    // Components can listen to these events to respond to changes in the
    // context of repositories. E.g. a c-table might have to update the data
    // that is displayed because after the context change the repository is
    // pointing to a different endpoint due to a context change.
    events = new EventTarget();

    update(updatedProps: IRepositoryParameters) {
        this.props = { ...this.props, ...updatedProps };

        // the below event will be removed soon, therefore please use the "context:updated" event instead
        this.events.dispatchEvent(
            new CustomEvent("updated", { detail: { ...this.props } })
        );

        this.events.dispatchEvent(
            new CustomEvent("context:updated", { detail: { ...this.props } })
        );
    }

    remove(propNames: string[]) {
        propNames.forEach((propName) => {
            if (propName in this.props) {
                delete this.props[propName];
            }
        });

        this.events.dispatchEvent(
            new CustomEvent("updated", { detail: { ...this.props } })
        );

        this.events.dispatchEvent(
            new CustomEvent("context:updated", { detail: { ...this.props } })
        );
    }

    // Returning a copy of properties so they cannot be accidentally changed.
    get(): IRepositoryParameters {
        return { ...this.props };
    }

    // Allows context consumers to wait on the presence of a certain property
    // before doing things.
    waitFor(propName: string): Promise<void> {
        return new Promise((resolve) => {
            // If the prop is already there, immediately resolve the promise.
            if (propName in this.get()) {
                resolve();
                return;
            }

            // Creating an object which contains our function, so that we can refer
            // to the same function the function using the reference to clean
            // up after ourselves once the promise is resolved.
            const ref = {
                fn: () => {
                    if (propName in this.get()) {
                        this.events.removeEventListener("updated", ref.fn);
                        resolve();
                    }
                },
            };

            this.events.addEventListener("updated", ref.fn);
        });
    }
}

// The 'call' signature describes a repository action in such a way so that
// it can be effectively used by the cache.
interface ICall {
    repositoryName: string;
    action: RepositoryAction;
    props: IRepositoryParameters;
    fields: IFields | FormData;
    response?: unknown;
}

interface ICache {
    // Each repository gets its own entry.
    [repositoryName: string]: {
        [key in RepositoryAction]: {
            // Every repository action has its own hashKey, to describe how it was
            // called. It always points to a promise that wraps the original request.
            [hashKey: string]: Readonly<Promise<Response | unknown>>;
        };
    };
}

// The repository cache is a layer on top of the repository finder, and
// provides functionality that can be used to modify repository action
// functions to cache repository action responses. It works with all
// repository actions, even custom or modified ones.
export class RepositoryServiceCache {
    // The default config for the cache. Currently it is configured to only
    // cache retrieval actions ('all' and 'get'), while invalidating those
    // actions on modifying actions.
    config: ICacheConfig = {
        cachedActions: {
            all: {
                "invalidate-on": [
                    "create",
                    "update",
                    "partialUpdate",
                    "delete",
                ],
            },
            get: {
                "invalidate-on": [
                    "create",
                    "update",
                    "partialUpdate",
                    "delete",
                ],
            },
        },
    };

    isCacheActive = false;
    cached: ICache = {};

    // Recursively sorts the keys in an object, so that the cache works for
    // deeply equal calls regardless of order.
    static sortObject(obj: IFields): IFields {
        const sortedObj: IFields = {};
        const sortedKeys = Object.keys(obj).sort();

        for (const key of sortedKeys) {
            const value = obj[key];
            if (
                typeof value === "object" &&
                !Array.isArray(value) &&
                value !== null
            ) {
                sortedObj[key] = RepositoryServiceCache.sortObject(value);
            } else {
                sortedObj[key] = value;
            }
        }

        return sortedObj;
    }

    // The function responsible for creating location hashes for specific
    // calls. It joins all properties and fields in a specific order and
    // stringifies it. When a call includes the same properties or fields, the
    // hash will be the same regardless of the order of the (nested) properties
    // or (nested) fields for the specific call.
    static getHashForCall(call: ICall) {
        const propsHashKey = RepositoryServiceCache.sortObject(
            call.props || {}
        );
        const fieldsHashKey = RepositoryServiceCache.sortObject(
            (call.fields as IFields) || {}
        );
        const hashKey =
            JSON.stringify(propsHashKey) + JSON.stringify(fieldsHashKey);
        return `${call.repositoryName}.${call.action}.${hashKey}`;
    }

    static hasFormData(call: ICall): call is ICall {
        return call.fields instanceof FormData;
    }

    activate() {
        this.isCacheActive = true;
    }

    clear() {
        Object.keys(this.cached).forEach((repositoryName) => {
            Object.keys(this.cached[repositoryName]).forEach(
                (actionToBeCleared) => {
                    delete this.cached[repositoryName][actionToBeCleared];
                }
            );
        });
    }

    // When deactivating the cache, also invalidate all entries so that upon
    // activating it again we don't accidentally serve old info.
    deactivate() {
        this.clear();
        this.isCacheActive = false;
    }

    isCached(call: ICall) {
        // FormData is not cacheable because it is not as easy to turn into a hash
        // compared to simple objects.
        if (call.fields instanceof FormData) {
            return undefined;
        }

        return !!this.getFromCache(call);
    }

    // Hashes a call and checks if it is present in the cache.
    getFromCache(call: ICall) {
        if (call.fields instanceof FormData) {
            return undefined;
        }

        const hash = RepositoryServiceCache.getHashForCall(call);
        return lodashGet(this.cached, hash);
    }

    // Checks whether the call should be cached, using the configuration for
    // the specific application.
    shouldBeCached(call: ICall) {
        if (!this.isCacheActive) {
            return false;
        }

        if (call.fields instanceof FormData) {
            return false;
        }

        if (
            "exclude" in this.config &&
            this.config.exclude.includes(call.repositoryName)
        ) {
            return false;
        }

        if (
            "cachedActions" in this.config &&
            call.action in this.config.cachedActions
        ) {
            return true;
        }

        return false;
    }

    // When cachable, creates a specific hash for a call and stores it in the
    // cache.
    addToCache(call: ICall) {
        if (!this.config) {
            throw Error(
                "Can't add something to the cache without a cache configuration."
            );
        }

        if (call.fields instanceof FormData) {
            throw Error("Can't cache calls which contain formdata.");
        }

        if (
            !(
                "cachedActions" in this.config &&
                call.action in this.config.cachedActions
            )
        ) {
            throw Error(
                `Won't cache the '${call.action}' call as it is not defined in the cache config.`
            );
        }

        if (!this.isCacheActive) {
            throw Error(
                `Won't cache the '${call.action}' call as the cache is not active.`
            );
        }

        const hash = RepositoryServiceCache.getHashForCall(call);
        lodashSet(this.cached, hash, call.response);
    }

    // Some calls can invalidate other entries. When repositories are generated
    // using a typical rest-based openapi schema, the default configuration
    // would invalidate 'GET' calls when 'non-GET' calls are done.
    invalidateUsing(call: ICall) {
        Object.entries(this.config.cachedActions).forEach(
            ([actionToBeCleared, actionConfig]) => {
                // If the repository cache is not configured to invalidate any calls
                // for the action of the current call, do nothing.
                if (!actionConfig["invalidate-on"].includes(call.action)) {
                    return;
                }

                // Iterate over all cached repositories.
                Object.keys(this.cached).forEach((repositoryName) => {
                    // If the given repository contains an action that should be cleared
                    // from the cache, do so.
                    if (actionToBeCleared in this.cached[repositoryName]) {
                        delete this.cached[repositoryName][actionToBeCleared];
                    }
                });
            }
        );
    }
}

class RepositoryFinder {
    repositories: IRepositoryContainer = {};
    context: RepositoryServiceContext = new RepositoryServiceContext();
    cache = new RepositoryServiceCache();

    addRepository(repoName: string, repo: IRepository) {
        if (repoName in this.repositories) {
            throw Error(`The repository '${repoName}' is already configured.`);
        }

        this.repositories[repoName] = repo;
    }

    findRepositoriesTargetingPathUsingMethod(
        path: RegExp,
        method: string
    ): IRepository[] {
        return findRepositoriesTargetingPathUsingMethod(
            this.repositories,
            path,
            method
        );
    }

    findRepositoriesWithSameLengthPathUsingMethod(
        path: string,
        method: string
    ): IRepository[] {
        return findRepositoriesWithSameLengthPathUsingMethod(
            this.repositories,
            path,
            method
        );
    }

    // To be able to associate a repository with a property name, the following
    // conditions should be true:
    // - The given propertyName should end with _id
    // - The to-be-associated repository should support a GET to retrieve a list
    // - The to-be-associated repository should support a GET to retrieve a specific
    //     item.
    // - The last "requiredParameter" of the repository (e.g. "producer_id" in
    //     "/products/{product_id}/producer/{producer_id}") should be equal to
    //     the given propertyName.
    findRepositoryNameForPropertyName(propertyName: string) {
        const relatedRepository = Object.entries(this.repositories).find(
            ([, repo]) => {
                // Assume the 'linkable' repository has both the get (GET on detail url)
                // and the all (GET on url) configured. Otherwise keep looking.
                if (!("get" in repo.actionPaths && "all" in repo.actionPaths)) {
                    return;
                }

                // The last required parameter in the url for the detail url typically
                // holds the name of the property name which we are trying to find
                // a repository for.
                //
                // E.g. if we are looking for the repository that is related to
                // production_location_id, the detail url for that repository will likely
                // contain an url like this:
                // /producers/{producer_id}/production-location/{production_location_id}
                //
                // Given that we are looking for a repository related to the
                // 'production_location_id field, we can conclude that the repository
                // that contains this url (and subsequently has 'production_location_id'
                // as the last item in its list of requiredParameters) is related to
                // this field.
                const lastRequiredParameter =
                    repo.actionPaths.get.requiredParameters[
                        repo.actionPaths.get.requiredParameters.length - 1
                    ];
                if (lastRequiredParameter === propertyName) {
                    return true;
                }
            }
        );

        if (relatedRepository) {
            // Take the repoName from [repoName, repo];
            return relatedRepository[0];
        }

        return undefined;
    }

    // Enrich repositories with information passed by the backend.
    enrichRepositoriesThroughBackendExtensions(repositories: IRepository[]) {
        repositories.forEach((repo: IRepository) => {
            actions.forEach((action) => {
                // There is nothing to enrich if a given action does not exist on a
                // repository or when there are no properties for a given action.
                if (
                    !(
                        action in repo.actionPaths &&
                        "properties" in repo.actionPaths[action]
                    )
                ) {
                    return;
                }

                this.enrichPropertiesThroughBackendExtensions(
                    repo.actionPaths[action].properties
                );
            });
        });
    }

    enrichPropertiesThroughBackendExtensions(
        properties: IStekzFieldProperties
    ) {
        // Iterate over all the properties for a given repository, to see
        // if they have any extensions present that should be applied.
        Object.values(properties).forEach(
            (properties: IStekzFieldProperties) => {
                // Nothing to enrich if there are no nested properties.
                if (typeof properties !== "object") {
                    return;
                }

                if ("properties" in properties) {
                    this.enrichPropertiesThroughBackendExtensions(
                        properties.properties
                    );
                }

                if (!("extensions" in properties)) {
                    return;
                }

                // When there are extensions present, only apply the enrichment
                // for a given extension when it exists.
                Object.entries(properties.extensions).forEach(
                    ([extensionName, extensionProperties]) => {
                        this.applyExtensionEnrichment(
                            extensionName,
                            properties,
                            extensionProperties
                        );
                    }
                );
            }
        );
    }

    // Adds 'x-translation' to individual fields that are translatable.
    addTranslationHints(
        extensionName: string,
        properties: IStekzFieldProperties,
        extensionProperties: IChoiceOptionsExtension
    ) {
        if ("properties" in properties && properties.type === "object") {
            this.addTranslationHints(
                extensionName,
                properties.properties,
                extensionProperties
            );
            return;
        }

        Object.keys(properties).forEach((languageCode) => {
            properties[languageCode]["x-translation"] = true;
        });
    }

    // Given the properties for a given field, enrich them through the extension.
    applyExtensionEnrichment(
        extensionName: string,
        properties: IStekzFieldProperties,
        extensionProperties: IChoiceOptionsExtension
    ) {
        // When the extension is 'xTranslatable', add a 'x-translation' hint
        // to the fields that are translatable.
        if (extensionName === "xTranslatable") {
            this.addTranslationHints(
                extensionName,
                properties,
                extensionProperties
            );
            return;
        }

        // Currently we're mostly dealing with the custom 'xChoiceOptions'
        // extension, which is an extension that provides a hint which can be
        // used to determine where the choices for a given field can be fetched
        // from.
        if (extensionName !== "xChoiceOptions") {
            return;
        }

        // Extract all the extension properties used by xChoiceOptions.
        const {
            href,
            method,
            key,
            "key-description": keyDescription,
        } = extensionProperties;

        // Both href and method are mandatory fields. If they are not present,
        // exit early.
        if (!href || !method) {
            return;
        }

        // Find the repository which employs both the href and the method
        // described by the extension.
        const match = Object.entries(this.repositories).find(
            ([repositoryName, repo]) => {
                // Find the 'action' that utilizes the given method. E.g. a 'create'
                // action might use the 'post' described in the 'method' in the
                // extension property.
                const matchedAction = actions.find((action) => {
                    if (!(action in repo.actionPaths)) {
                        return false;
                    }

                    return repo.actionPaths[action].method === method;
                });

                if (!matchedAction) {
                    return false;
                }

                // If there is a matched action, also make sure that the 'href' in the
                // extension corresponds to the 'href' for the given action.
                if (
                    !("path" in repo.actionPaths[matchedAction]) ||
                    repo.actionPaths[matchedAction].path !== href
                ) {
                    return false;
                }

                // At this point, we're totally sure which repository the backend is
                // talking about in its extension.
                return repositoryName;
            }
        );

        // If there is a related repository name, add 'service-repository' to
        // the properties of this field. This can e.g help choice fields to
        // pre-populate themselves by doing a request to this repository.
        if (match) {
            const relatedRepositoryName = match[0];
            properties["service-repository"] = relatedRepositoryName;

            // This one is optional; some choice fields do not use 'id' as a key,
            // in that case, the backend can specify what it does want us to use
            // instead.
            if (key) {
                properties["key"] = key;
            }
            if (keyDescription) {
                properties["key-description"] = keyDescription;
            }
        }
    }

    // Check all property names and their properties to check whether they
    // can be enriched with a service-repository configuration to leave a hint
    // to a specific field to let components know that they can retrieve
    // values for that field from a different repository.
    linkPropertyFieldsToRepositories(
        repo: IRepository,
        properties: IStekzFieldProperties
    ) {
        Object.entries(properties).forEach(
            ([propertyName, fieldProperties]) => {
                // Nothing to enrich if there are no nested properties.
                if (typeof fieldProperties !== "object") {
                    return;
                }

                // Do the same recursively for nested fields.
                if (
                    ["object", "array"].includes(fieldProperties.type) &&
                    "properties" in fieldProperties
                ) {
                    this.linkPropertyFieldsToRepositories(
                        repo,
                        fieldProperties.properties
                    );
                    return;
                }

                // No need to configure a new service-repository when it already
                // exists. It could already exist when these fieldProperties are
                // already enriched through e.g. a backend extension.
                if ("service-repository" in fieldProperties) {
                    return;
                }

                if (!propertyName.endsWith(propertyToEnrichWithLinkEndsWith)) {
                    return;
                }

                // Assume that the field describes a foreign key when one of the
                // following is true:
                // - it is a string and has a format of uuid4
                // - it is an integer
                let linkableField = false;
                if (
                    "format" in fieldProperties &&
                    fieldProperties.format === "uuid4" &&
                    fieldProperties.type === "string"
                ) {
                    linkableField = true;
                } else if (fieldProperties.type === "integer") {
                    linkableField = true;
                }

                if (!linkableField) {
                    return;
                }

                const relatedRepositoryName =
                    this.findRepositoryNameForPropertyName(propertyName);

                // If there is a related repository name, we add it to the fieldProperties
                // of the given propertyName.
                if (relatedRepositoryName) {
                    fieldProperties["service-repository"] =
                        relatedRepositoryName;
                }
            }
        );
    }

    // Try to guess which properties could be enriched with an association to
    // another repository. This makes it easy for custom elements to grab the
    // appropriate associated repository, for e.g. Foreign Keys in forms.
    //
    // For extra documentation, visit:
    // https://my.stekz.com/Stekz/steks/MyStekz/biz/BizDevOps/products/components#repository-linking
    linkRepositories(repositories: IRepository[]) {
        repositories.forEach((repo: IRepository) => {
            actionsWhosePropertiesCouldBeEnrichedWithLinks.forEach((action) => {
                // There are only a few actions that require their properties to be
                // enriched with a linked repository.
                if (
                    !(
                        action in repo.actionPaths &&
                        "properties" in repo.actionPaths[action]
                    )
                ) {
                    return;
                }

                this.linkPropertyFieldsToRepositories(
                    repo,
                    repo.actionPaths[action].properties
                );
            });
        });
    }

    // Fetch a custom repository by the name that is defined in the project
    // configuration and add it to the map of repositories.
    // For more info, check out
    // https://my.stekz.com/Stekz/steks/MyStekz/biz/BizDevOps/products/components/project_configuration.md#service-repository-type-custom
    async configureCustomRepository<T extends IRepository>(
        repositoryName: string,
        Repo: new () => T
    ) {
        const repo = new Repo();
        this.addRepository(repositoryName, repo);
    }

    // Fetch an openapi spec and parse a repository. The openapi spec should
    // contain the paths defined in the configuration, and using these paths
    // a repository is parsed. It basically maps the POST/GET/GET/PUT/DELETE to
    // different actions on a repository; repository.objects.create (POST),
    // repository.objects.get (GET), repository.objects.all (GET),
    // repository.objects.update (PUT), repository.objects.delete (DELETE).
    // For more info, check out
    // https://my.stekz.com/Stekz/steks/MyStekz/biz/BizDevOps/products/components/project_configuration.md#service-repository-type-openapi
    async configureOpenApiRepository(
        repositoryName: string,
        { paths, openApiFileLocation }: IConfig
    ) {
        try {
            const repo: OpenAPIGeneratedRepository =
                await parsers.createRepositoryFromOpenApiSpec(
                    repositoryName,
                    openApiFileLocation,
                    paths
                );
            this.addRepository(repositoryName, repo);
        } catch (e) {
            if (e instanceof RepositoryCreationError) {
                console.error(
                    `Failed to create repository '${repositoryName}'.`
                );
            } else {
                throw e;
            }
        }
    }

    // This iterates over all functions in the repository manager to modify
    // their functions. Basically, this magic adds some extra parameters to the
    // 'props' argument of repository manager functions. This way custom or
    // generated repositories don't need to have additional logic implemented
    // to make use of these parameters.
    modifyRepositoryManagerToInjectParameters(
        repositoryObjects: IRepositoryManager
    ) {
        // Create an alias to this finder, this way repository managers can access
        // 'live' properties that are stored on this finder while still being
        // able to use their own 'this'. We're basically mixing contexts on
        // purpose.
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const thisRef = this;
        actions.forEach((action) => {
            if (!(action in repositoryObjects)) {
                return;
            }

            // Create a reference to the original function.
            const managerFunction = repositoryObjects[action];

            // Now modify the original function so that any time someone calls it
            // the 'props argument' will be extended with our parameters.
            repositoryObjects[action] = function (
                props: IRepositoryParameters,
                ...args
            ) {
                const newProps = { ...thisRef.context.get(), ...props };
                // To avoid scoping the managerFunction to this finder, apply the
                // new arguments in the context of the repository manager through
                // apply. Then, update the arguments that are to be passed to these
                // functions.
                return managerFunction.apply(repositoryObjects, [
                    newProps,
                    ...args,
                ]);
            };
        });

        // When there is a function that checks whether all parameters are present
        // that are required for a specific repository action, also enrich that
        // function the same way as we're enriching the individual methods.
        if ("test" in repositoryObjects) {
            const propertyTestFunction = repositoryObjects.test;
            repositoryObjects.test = function (
                action: string,
                props: IRepositoryParameters
            ) {
                const newProps = { ...thisRef.context.get(), ...props };
                return propertyTestFunction.apply(repositoryObjects, [
                    action,
                    newProps,
                ]);
            };
        }
    }

    // This iterates over all functions in the repository manager to modify
    // their functions. This modifies the repository actions on the repository
    // manager to add a caching layer.
    modifyRepositoryManagerToCacheActions(
        repositoryObjects: IRepositoryManager,
        repositoryName: string
    ) {
        // Create an alias to this finder, this way repository managers can access
        // the cache while still being able to use their own 'this'. We're
        // basically mixing contexts on purpose.
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const thisRef = this;
        actions.forEach((action) => {
            if (!(action in repositoryObjects)) {
                return;
            }

            // Create a reference to the original function.
            const managerFunction = repositoryObjects[action];

            // Replace the pointer to the original function to a new one which wraps
            // the original and adds caching functionality to it.
            repositoryObjects[action] = async function (
                props: IRepositoryParameters,
                fields: IFields,
                options?: IRepositoryActionOptions
            ) {
                // The call below is used as a signature for the caching functions.
                const call: ICall = {
                    action: action as RepositoryAction,
                    repositoryName,
                    props,
                    fields,
                };

                // Certain calls should invalidate other entries in the cache.
                thisRef.cache.invalidateUsing(call);

                // If the specific call should not be cached, call the original
                // function and do nothing special.
                if (!thisRef.cache.shouldBeCached(call)) {
                    return managerFunction.apply(repositoryObjects, [
                        props,
                        fields,
                    ]);
                }

                const isCached = thisRef.cache.isCached(call);
                // When the call is not yet cached, or when a repository action is
                // called with the specific 'renewCacheEntry' option, call the original
                // function and store a pointer to it in the cache.
                if (!isCached || options?.renewCacheEntry) {
                    call["response"] = managerFunction.apply(
                        repositoryObjects,
                        [props, fields]
                    );
                    thisRef.cache.addToCache(call);
                }

                // Most of the time cached responses are wrapped in a promise, but
                // sometimes they are not. Using await here works for both sync and
                // async responses.
                const response = await thisRef.cache.getFromCache(call);

                // While it's not the nicest way, sometimes repository modifications
                // replace the json function of the response so that it can be used
                // again, while leaving the response as is. In that case, there's
                // nothing we can do to preserve the original response and we'll have
                // to rely on the modification to return a proper json method.
                if (response.bodyUsed) {
                    return response;
                }

                // When there is a response which body is not used, we can return a
                // cloned version of it so that it can be used multiple times.
                if (response instanceof Response) {
                    return response.clone();
                }

                return response;
            };
        });
    }

    // Configure all custom functions that need to live on 'objects'.
    modifyRepositoryObjects(
        repositoryObjects: IRepositoryManager,
        repositoryModificationsObjects: IModificationsObjects
    ) {
        Object.entries(repositoryModificationsObjects).forEach(
            ([method, fn]) => {
                if (!(method in repositoryObjects.repository.actionPaths)) {
                    throw Error(
                        `Cannot apply a modification to the non-existing '${method}' action on the '${repositoryObjects.repository.name}' repository.`
                    );
                }

                repositoryObjects[method] = fn;
            }
        );
    }

    // Configure all custom getters that need to live on 'properties'.
    modifyRepositoryProperties(
        repositoryProperties: IRepositoryPropertiesChecker,
        repositoryModificationsProperties: IModificationsProperties
    ) {
        Object.entries(repositoryModificationsProperties).forEach(
            ([method, fn]) => {
                if (!(method in repositoryProperties.repository.actionPaths)) {
                    throw Error(
                        `Cannot apply a modification to the non-existing '${method}' properties on the '${repositoryProperties.repository.name}' repository.`
                    );
                }

                Object.defineProperty(repositoryProperties, method, {
                    get: <() => IStekzSchema>fn,
                });
            }
        );
    }

    // Modify the repository action properties with the order that is passed in the repository ordering object
    modifyRepositoryActionPaths(
        repositoryActionPaths: ActionPaths,
        repositoryModificationsOrderObj: IModifications,
        repositoryName: string
    ) {
        Object.keys(repositoryModificationsOrderObj).forEach((action) => {
            if (!(action in repositoryActionPaths)) {
                monitoring.error(
                    `Can't apply the ordering modification to the '${action}' method on the '${repositoryName}' repository, since that method does not exist.`
                );
                return;
            }

            const defaultProperties = repositoryActionPaths[action].properties;
            const orderingArr = repositoryModificationsOrderObj[action];
            const defaultPropertyKeys = Object.keys(defaultProperties);

            // order the propeties in the order that is passed in the modification order object
            const orderedPropertiesObj = {};
            orderingArr.forEach((field) => {
                if (field in defaultProperties) {
                    orderedPropertiesObj[field] = defaultProperties[field];
                }
            });

            // find the missing fields in the order object action value and
            // append those fields in the end of the orderedPropertiesObj
            const missingProperyObjectKeys = defaultPropertyKeys.filter(
                (x) => orderingArr.indexOf(x) === -1
            );
            missingProperyObjectKeys.forEach((property) => {
                orderedPropertiesObj[property] = defaultProperties[property];
            });

            repositoryActionPaths[action].properties = orderedPropertiesObj;
        });
    }

    // The config mentioned typically refers to the service configuration that
    // is part of the application configuration which always lives in the
    // root of the project. E.g. in some-domain.com/config.js.
    async fromConfig(config: { [repositoryName: string]: IConfig }) {
        const repositoriesThatShouldNotBeLinked: string[] = [];

        // promises stores a list of instructions that can be executed async
        const promises = [];
        const finder = new RepositoryFinder();

        const repositoryModifications: IModifications = {};
        // read config to add repositories, fetch all required repositories in parallel.
        Object.entries(config).forEach(([repositoryName, repositoryConfig]) => {
            if (repositoryConfig.avoidLinking) {
                repositoriesThatShouldNotBeLinked.push(repositoryName);
            }

            // Check if a repository configuration is valid before configuring it.
            checkRepositoryConfiguration(
                repositoryName,
                repositoryConfig.type,
                repositoryConfig
            );
            if (repositoryConfig.type === SupportedConfigTypes.Custom) {
                promises.push(this.configureCustomRepository(repositoryName));
            } else if (repositoryConfig.type === SupportedConfigTypes.OpenApi) {
                promises.push(
                    this.configureOpenApiRepository(
                        repositoryName,
                        repositoryConfig
                    )
                );
            }

            // Collect all the modifications that need to be applied to this
            // repository.
            const modifications = collectRepositoryModifications(
                repositoryName,
                repositoryConfig,
                promises
            );
            if (modifications) {
                repositoryModifications[repositoryName] = modifications;
            }
        });

        // Wait until all async instructions for fetching and adding the repositories
        // are complete.
        await Promise.all(promises);

        const repositories = Object.entries(this.repositories);

        // There might be custom modifications that need to be applied to
        // repositories, apply them if necessary.
        repositories.forEach(([repoName, repo]) => {
            if (!(repoName in repositoryModifications)) {
                return;
            }

            // Replace the functions on repo.objects
            if (repositoryModifications[repoName].objects) {
                this.modifyRepositoryObjects(
                    repo.objects,
                    repositoryModifications[repoName].objects
                );
            }

            // Replace the getters on repo.properties
            if (repositoryModifications[repoName].properties) {
                this.modifyRepositoryProperties(
                    repo.properties,
                    repositoryModifications[repoName].properties
                );
            }

            if (repositoryModifications[repoName].ordering) {
                // Replace actionPaths to change the ordering of properties
                this.modifyRepositoryActionPaths(
                    repo.actionPaths,
                    repositoryModifications[repoName].ordering,
                    repoName
                );
            }
        });

        // When the cache object exists, enrich the repository manager so it can
        // optionally cache or return items from the cache.
        if (this.cache) {
            repositories.forEach(([repoName, repo]) => {
                this.modifyRepositoryManagerToCacheActions(
                    repo.objects,
                    repoName
                );
            });
        }

        repositories.forEach(([, repo]) => {
            this.modifyRepositoryManagerToInjectParameters(repo.objects);
        });

        this.enrichRepositoriesThroughBackendExtensions(
            repositories.map(([, repo]) => repo)
        );

        // filter out repositories that have 'avoidLinking' set to true in their
        // respective configuration.
        const repositoriesThatCouldBeLinked = repositories
            .filter(
                ([repoName, ,]) =>
                    !repositoriesThatShouldNotBeLinked.includes(repoName)
            )
            // Convert the filtered repositories list of lists into a simple
            // repository list.
            .map(([, repo]) => repo);

        this.linkRepositories(repositoriesThatCouldBeLinked);

        return finder;
    }
}

export default RepositoryFinder;
